import { useMemo } from 'react';
import { type DropEvent, useDropzone } from 'react-dropzone';

import { type FileUploadCommonProps, type FileUploadMethod } from './file-upload.types';
import { normalizeAccept } from './normalize-accept';

type DropzoneMethod = FileUploadMethod | ((event: DropEvent) => FileUploadMethod);

export interface UseFileUploadDropzoneOptions extends Pick<
  FileUploadCommonProps,
  | 'accept'
  | 'multiple'
  | 'maxFiles'
  | 'maxSize'
  | 'minSize'
  | 'disabled'
  | 'onFilesAccepted'
  | 'onFilesRejected'
> {
  method: DropzoneMethod;
  normalizeFile?: (file: File) => File;
  noClick?: boolean;
  noDrag?: boolean;
  noKeyboard?: boolean;
}

const filesFromDropEvent = (event: DropEvent): File[] => {
  if (Array.isArray(event)) return [];

  const dataTransfer =
    'dataTransfer' in event && event.dataTransfer ? event.dataTransfer : undefined;
  if (dataTransfer?.files) {
    const files = Array.from(dataTransfer.files);
    if (files.length > 0) return files;
  }
  if (dataTransfer?.items) {
    return Array.from(dataTransfer.items)
      .filter((item) => item.kind === 'file')
      .map((item) => item.getAsFile())
      .filter((file): file is File => file != null);
  }

  const target =
    typeof HTMLInputElement !== 'undefined' &&
    'target' in event &&
    event.target instanceof HTMLInputElement
      ? event.target
      : undefined;
  if (target?.files) {
    return Array.from(target.files);
  }

  return [];
};

/**
 * Shared react-dropzone wiring for every file-upload primitive. Not exported
 * from the door — callers use `FileDropzone`, `FileUploadButton`, or
 * `FileDropSurface` so validation cannot be skipped.
 */
export function useFileUploadDropzone({
  accept,
  multiple = false,
  maxFiles = 0,
  maxSize,
  minSize,
  disabled = false,
  onFilesAccepted,
  onFilesRejected,
  method,
  normalizeFile,
  noClick,
  noDrag,
  noKeyboard,
}: UseFileUploadDropzoneOptions) {
  const normalizedAccept = useMemo(
    () => (accept != null ? normalizeAccept(accept) : undefined),
    [accept]
  );

  const dropzone = useDropzone({
    multiple,
    accept: normalizedAccept,
    maxFiles,
    maxSize,
    minSize,
    disabled,
    ...(noClick ? { noClick } : {}),
    ...(noDrag ? { noDrag } : {}),
    ...(noKeyboard ? { noKeyboard } : {}),
    ...(normalizeFile
      ? {
          // FileDropSurface is drag-only; this normalizes concrete dropped files
          // and does not promise directory expansion or file path metadata.
          getFilesFromEvent: (event) =>
            Promise.resolve(filesFromDropEvent(event).map(normalizeFile)),
        }
      : {}),
    onDropAccepted: (files, event) =>
      onFilesAccepted?.(files, {
        method: typeof method === 'function' ? method(event) : method,
      }),
    onDropRejected: (rejections) => onFilesRejected?.(rejections),
  });

  return { ...dropzone, normalizedAccept };
}

/**
 * react-dropzone funnels both real drops and file-picker selections through
 * `onDropAccepted`. The event is the only discriminator. Picker selections
 * arrive as an input change event or (with the File System Access API) an
 * array of file handles — both mean browse.
 */
export function fileUploadMethodFromDropEvent(event: DropEvent): FileUploadMethod {
  return !Array.isArray(event) && 'type' in event && event.type === 'drop'
    ? 'drag_drop'
    : 'browse';
}
